import React, { useState, useEffect, useCallback } from 'react';
import '../css/Components.css';
import { BsTools } from "react-icons/bs";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { API_URLS } from '../config';
import { apiRequest } from '../utils/apiRequest';
import { useAuth } from '../context/AuthContext';

/**
 * Competency component — connected to real API.
 *
 * Props:
 *   value    : { [disciplineId]: { [subDisciplineId]: productId[] } }
 *   onChange : (newValue) => void
 *   readOnly : boolean (optional)
 *
 * Data shape fetched:
 *   disciplines   : [{ id, name }]
 *   subDisciplines: [{ id, name, disciplineId }]
 *   products      : [{ id, name, subDisciplineId, disciplineId }]
 */

const Competency = ({ value = {}, onChange, readOnly = false }) => {
  const { token } = useAuth();

  // ── UI state ───────────────────────────────────────────────────────────────
  const [collapsed, setCollapsed] = useState(false);
  const [expandedDisc, setExpandedDisc] = useState({});  // disciplineId → bool

  // ── Data state ─────────────────────────────────────────────────────────────
  const [disciplines, setDisciplines] = useState([]);
  const [subDisciplines, setSubDisciplines] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Fetch all master data (no pagination — load all for selector) ──────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [discRes, subRes, prodRes] = await Promise.all([
        apiRequest(`${API_URLS.DISCIPLINES}?limit=1000`, {}, token),
        apiRequest(`${API_URLS.SUBDISCIPLINES}?limit=1000`, {}, token),
        apiRequest(`${API_URLS.PRODUCTS}?limit=1000`, {}, token),
      ]);

      if (discRes?.ok) setDisciplines(discRes.data.rows || []);
      if (subRes?.ok) setSubDisciplines(subRes.data.rows || []);
      if (prodRes?.ok) setProducts(prodRes.data.rows || []);

      if (!discRes?.ok || !subRes?.ok || !prodRes?.ok) {
        setError('Could not load competency data. Check your permissions.');
      }
    } catch {
      setError('Network error loading competency data.');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getSubsByDisc = (discId) =>
    subDisciplines.filter(s => String(s.disciplineId) === String(discId));

  const getProductsBySub = (subId) =>
    products.filter(p => String(p.subDisciplineId) === String(subId));

  // Check if a product is selected
  const isProductSelected = (discId, subId, prodId) => {
    return (value[discId]?.[subId] || []).includes(prodId);
  };

  // Toggle a single product
  const toggleProduct = (discId, subId, prodId) => {
    if (readOnly) return;
    const current = value[discId]?.[subId] || [];
    const updated = current.includes(prodId)
      ? current.filter(id => id !== prodId)
      : [...current, prodId];

    const newValue = {
      ...value,
      [discId]: {
        ...(value[discId] || {}),
        [subId]: updated,
      },
    };
    onChange?.(newValue);
  };

  // Count selected products for a sub-discipline
  const countSelected = (discId, subId) =>
    (value[discId]?.[subId] || []).length;

  // Count total selected products for a discipline
  const countDiscSelected = (discId) => {
    const subs = getSubsByDisc(discId);
    return subs.reduce((acc, s) => acc + countSelected(discId, s.id), 0);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="competency-wrapper">
      {/* Header */}
      <div
        className="competency-header"
        onClick={() => setCollapsed(c => !c)}
        style={{ cursor: 'pointer' }}
      >
        <h2><BsTools /> Competency</h2>
        <span>{collapsed ? <FaChevronDown /> : <FaChevronUp />}</span>
      </div>

      {!collapsed && (
        <>
          {loading && (
            <div style={{ padding: '16px 20px', color: '#888', fontSize: 14 }}>
              Loading competency data…
            </div>
          )}

          {error && (
            <div style={{ padding: '12px 20px', color: '#c0392b', fontSize: 13 }}>
              ⚠ {error}
            </div>
          )}

          {!loading && !error && disciplines.length === 0 && (
            <div style={{ padding: '12px 20px', color: '#888', fontSize: 13 }}>
              No disciplines found. Add disciplines in the Master module first.
            </div>
          )}

          {!loading && !error && disciplines.map(disc => {
            const subs = getSubsByDisc(disc.id);
            const discExpanded = !!expandedDisc[disc.id]; // default collapsed
            const discSelected = countDiscSelected(disc.id);

            return (
              <div key={disc.id} className="discipline-section">
                {/* Discipline Header */}
                <div
                  className="discipline-header"
                  onClick={() => setExpandedDisc(e => ({ ...e, [disc.id]: !discExpanded }))}
                  style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <strong>
                    Discipline: {disc.name}
                    {discSelected > 0 && (
                      <span style={{
                        marginLeft: 8,
                        background: '#1a2a4d',
                        color: '#fff',
                        borderRadius: 10,
                        padding: '1px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                      }}>
                        {discSelected} selected
                      </span>
                    )}
                  </strong>
                  <span>{discExpanded ? <FaChevronUp /> : <FaChevronDown />}</span>
                </div>

                {discExpanded && (
                  <>
                    {subs.length === 0 && (
                      <div style={{ padding: '8px 20px', color: '#aaa', fontSize: 13 }}>
                        No sub-disciplines under this discipline.
                      </div>
                    )}

                    <div className="sub-disciplines-grid">
                      {subs.map(sub => {
                        const subProds = getProductsBySub(sub.id);
                        const selCount = countSelected(disc.id, sub.id);

                        return (
                          <div key={sub.id} className="sub-discipline-container">
                            {/* Sub-Discipline Header */}
                            <div
                              className="sub-discipline-header"
                              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                Sub-Discipline: {sub.name}
                                {selCount > 0 && (
                                  <span style={{
                                    background: '#e8f0fe',
                                    color: '#1a2a4d',
                                    borderRadius: 10,
                                    padding: '1px 7px',
                                    fontSize: 11,
                                    fontWeight: 600,
                                  }}>
                                    {selCount}/{subProds.length}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Products Grid */}
                            <>
                              <div style={{ padding: '5px 10px', fontSize: 12 , borderBottom: '1px solid #ccc' }}>Products :</div>
                              {subProds.length === 0 ? (
                                <div style={{ padding: '8px 20px', color: '#aaa', fontSize: 12 }}>
                                  No products under this sub-discipline.
                                </div>
                              ) : (
                                <div className="grid-table" style={{ padding: '0' }}>
                                  {subProds.map(prod => (
                                    <label key={prod.id} className="checkbox-item">
                                      <input
                                        type="checkbox"
                                        checked={isProductSelected(disc.id, sub.id, prod.id)}
                                        onChange={() => toggleProduct(disc.id, sub.id, prod.id)}
                                        disabled={readOnly}
                                      />
                                        {prod.name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default Competency;